import React, { useEffect, useState } from "react";
import {
  collection, collectionGroup, query, where,
  onSnapshot, doc, getDocs, addDoc, updateDoc,
} from "firebase/firestore";
import { firestore } from "../shared/FirebaseConfig";
import { useUser } from "../shared/UserContext";
import { findTeacherClass } from "../shared/classForStudent";
import { avatarColor, initials } from "../shared/avatar";
import { relativeWhen } from "../shared/dateLabels";
import "./ActiveSignOuts.css";

async function findStudent(idNum, uid, schoolId) {
  const ref = collection(firestore, "students");
  // Your own roster first (covers private numbering)…
  for (const q of [
    query(ref, where("studentId", "==", idNum), where("addedBy", "==", uid)),
    query(ref, where("studentId", "==", idNum), where("teachers", "array-contains", uid)),
  ]) {
    const snap = await getDocs(q);
    if (!snap.empty) return { docId: snap.docs[0].id, ...snap.docs[0].data() };
  }
  // …then the school directory (shared students), same as sign-out.
  if (schoolId) {
    const snap = await getDocs(
      query(ref, where("studentId", "==", idNum), where("schoolId", "==", schoolId))
    );
    const match = snap.docs.find(d => d.data().shared !== false);
    if (match) return { docId: match.id, ...match.data() };
  }
  return null;
}

// ── Checkout Form ────────────────────────────────────────────────────────────
function CheckoutForm({ tracker, user, schoolId }) {
  const [studentId, setStudentId]     = useState("");
  const [fieldValues, setFieldValues] = useState({});
  const [error, setError]             = useState("");
  const [submitting, setSubmitting]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const student = await findStudent(studentId.trim(), user.uid, schoolId);
      if (!student) { setError("Student not found. Check the ID and try again."); return; }

      // Attribute the loan to the class THIS teacher has the student in
      // (their own roster class), not the student's first enrolled class.
      const cls = await findTeacherClass(student, user.uid);
      if (!cls) { setError("Student has no enrolled class."); return; }
      const classId = cls.id;
      const classData = cls;

      await addDoc(collection(firestore, `classes/${classId}/lendingRecords`), {
        trackerId:              tracker.id,
        trackerName:            tracker.name,
        studentDocumentNumber:  student.docId,
        studentName:            student.studentName,
        studentId:              studentId.trim(),
        fieldValues,
        time_out:               new Date().toISOString(),
        time_in:                null,
        teacherUID:             user.uid,
        classId,
        className:              classData.className ?? "",
      });

      setStudentId("");
      setFieldValues({});
    } catch (err) {
      console.error("Checkout error:", err);
      setError("Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const color = tracker.color || "var(--color-primary)";

  return (
    <div className="form-container" style={{ borderTop: `6px solid ${color}` }}>
      <h2 style={{ color }}>{tracker.name}</h2>
      <form className="form" onSubmit={handleSubmit}>
        <div className="form-row-input">
          <input
            type="text"
            placeholder="Student ID"
            value={studentId}
            onChange={e => { setStudentId(e.target.value); setError(""); }}
            required
          />
          {tracker.fields.map(f => (
            <input
              key={f.label}
              type="text"
              placeholder={f.label}
              value={fieldValues[f.label] || ""}
              onChange={e => setFieldValues(prev => ({ ...prev, [f.label]: e.target.value }))}
              required
            />
          ))}
        </div>
        {error && (
          <p style={{ color: "var(--color-error)", fontSize: 14, margin: "8px 0 0" }}>{error}</p>
        )}
        <button type="submit" disabled={submitting}>
          {submitting ? "Checking out…" : "Check Out"}
        </button>
      </form>
    </div>
  );
}

// ── Active Loans Table ───────────────────────────────────────────────────────
function ActiveLoansTable({ tracker, uid }) {
  const [loans, setLoans] = useState([]);

  useEffect(() => {
    if (!uid) return;
    // All of this tracker's loans created by this teacher, regardless of which
    // class the record is nested under. Records carry teacherUID + className.
    return onSnapshot(
      query(collectionGroup(firestore, "lendingRecords"), where("teacherUID", "==", uid)),
      snap => {
        const active = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(r => r.trackerId === tracker.id && r.time_in === null);
        setLoans(active);
      }
    );
  }, [uid, tracker.id]);

  const handleReturn = async (loan) => {
    try {
      await updateDoc(
        doc(firestore, `classes/${loan.classId}/lendingRecords/${loan.id}`),
        { time_in: new Date().toISOString() }
      );
    } catch (err) {
      console.error("Return error:", err);
    }
  };

  const accent = tracker.color || "var(--color-primary)";
  const fieldBg = tracker.color ? `${tracker.color}2b` : "#eef1f4";   // stronger tint of the tracker color
  const fieldBorder = tracker.color ? `${tracker.color}99` : "#d8dee5"; // matching outline so it reads per-tracker
  // Per-tracker overdue threshold (days); falls back to 14 for older trackers.
  const OVERDUE_MS = (tracker.overdueDays ?? 14) * 24 * 60 * 60 * 1000;

  return (
    <div className="display-sign-out-container">
      <div className="signout-header">
        <h2 style={{ margin: 0 }}>
          Currently Out
          {loans.length > 0 && <span className="signout-count">{loans.length} out</span>}
        </h2>
      </div>

      {loans.length === 0 ? (
        <div className="signout-empty">Nothing currently checked out.</div>
      ) : (
        <div className="signout-list">
          {[...loans].sort((a, b) => new Date(a.time_out) - new Date(b.time_out)).map(loan => {
            const overdue = Date.now() - new Date(loan.time_out) > OVERDUE_MS;
            return (
              <div key={loan.id} className="signout-card" style={{ borderLeft: `4px solid ${accent}` }}>
                <span className="signout-avatar" style={{ background: avatarColor(loan.studentName) }}>
                  {initials(loan.studentName)}
                </span>
                <span className="signout-body">
                  <span className="signout-name">{loan.studentName}</span>
                  {loan.className && <span className="signout-sub">{loan.className}</span>}
                </span>
                {tracker.fields.map(f => {
                  const v = loan.fieldValues?.[f.label];
                  return v ? (
                    <span key={f.label} className="loan-field" style={{ background: fieldBg, borderColor: fieldBorder }}>
                      {f.label}: <b>{v}</b>
                    </span>
                  ) : null;
                })}
                {overdue && <span className="overdue-pill">Overdue</span>}
                <span className={`card-when ${overdue ? "is-overdue" : ""}`}>Out {relativeWhen(loan.time_out)}</span>
                <span className="signout-action">
                  <button className="signout-return-btn" onClick={() => handleReturn(loan)}>
                    Return
                  </button>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function LendingPage() {
  const { user, uid, schoolId } = useUser();
  const [trackers, setTrackers]       = useState([]);
  const [activeTab, setActiveTab]     = useState(null);
  const [loading, setLoading]         = useState(true);

  // Remember the last-used tracker per device, so a station parked at the
  // Chromebook cart opens straight to that tracker.
  const storageKey = uid ? `lending-tracker-${uid}` : null;

  useEffect(() => {
    if (!uid) return;
    return onSnapshot(doc(firestore, "users", uid), snap => {
      const t = snap.data()?.lendingTrackers || [];
      setTrackers(t);
      setActiveTab(prev => {
        if (prev && t.find(x => x.id === prev)) return prev;
        const saved = storageKey ? localStorage.getItem(storageKey) : null;
        if (saved && t.find(x => x.id === saved)) return saved;
        return t[0]?.id ?? null;
      });
      setLoading(false);
    });
  }, [uid, storageKey]);

  const selectTab = (id) => {
    setActiveTab(id);
    if (storageKey) localStorage.setItem(storageKey, id);
  };

  if (loading) return <div>Loading…</div>;

  if (!trackers.length) {
    return (
      <div className="content-container" style={{ textAlign: "center", paddingTop: 60 }}>
        <h2>No Lending Trackers Set Up</h2>
        <p style={{ color: "var(--color-secondary)", marginBottom: 20 }}>
          Go to <strong>Settings → Lending Trackers</strong> to create your first tracker
          (e.g. Chromebooks, Calculators, Class Library).
        </p>
      </div>
    );
  }

  const currentTracker = trackers.find(t => t.id === activeTab) || trackers[0];

  return (
    <div className="content-container">
      {trackers.length > 1 && (
        <div style={{ display: "flex", gap: 0, borderBottom: "2px solid #eee", marginBottom: 24, overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          {trackers.map(t => (
            <button
              key={t.id}
              onClick={() => selectTab(t.id)}
              style={{
                flexShrink: 0, whiteSpace: "nowrap",
                background: "none", border: "none",
                borderBottom: t.id === activeTab ? `3px solid ${t.color || "var(--color-primary)"}` : "2px solid transparent",
                marginBottom: -2,
                padding: "10px 20px",
                fontWeight: t.id === activeTab ? 700 : 400,
                color: t.id === activeTab ? (t.color || "var(--color-primary)") : "var(--color-secondary)",
                cursor: "pointer", fontSize: 15,
              }}
            >
              <span style={{
                display: "inline-block", width: 10, height: 10, borderRadius: "50%",
                background: t.color || "var(--color-primary)", marginRight: 7,
              }} />
              {t.name}
            </button>
          ))}
        </div>
      )}

      <div className="split-layout">
        <div className="split-form">
          <CheckoutForm tracker={currentTracker} user={user} schoolId={schoolId} />
        </div>
        <div className="split-list">
          <ActiveLoansTable tracker={currentTracker} uid={uid} />
        </div>
      </div>
    </div>
  );
}
